import React, {
  ChangeEvent,
  forwardRef,
  ReactElement,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { cn } from 'src/lib/utils';

type InputFileProps = {
  id: string;
  accept: string;
  onFileUpload: (file: File) => void;
  disabled?: boolean;
};

export type InputFileHandle = {
  clear: () => void;
};

export const InputFile = forwardRef<InputFileHandle, InputFileProps>(
  ({ id, accept, onFileUpload, disabled }, ref): ReactElement => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [fileName, setFileName] = useState<string>('');

    useImperativeHandle(ref, () => ({
      clear: () => {
        setFileName('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      },
    }));

    const handleWrapperClick = () => {
      if (fileInputRef.current && !disabled) {
        fileInputRef.current.click();
      }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setFileName(file.name);
        onFileUpload(file);
      } else {
        setFileName('');
      }
      if (fileInputRef.current) {
        fileInputRef.current.blur();
      }
    };

    return (
      <div
        className={cn(
          'relative flex h-10 w-full cursor-pointer items-center gap-2.5 rounded-md border border-border-default bg-layout px-3 py-2 text-sm text-layout-foreground',
          'focus-within:border-border-default focus-within:ring-2 focus-within:ring-primary-focus',
          disabled && 'cursor-not-allowed',
        )}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleWrapperClick();
          }
        }}
        role='button'
        tabIndex={0}
        onClick={handleWrapperClick}
      >
        <Button
          variant='ghost'
          disabled={disabled}
          onClick={handleWrapperClick}
          className='pl-1 pr-0 font-medium tracking-tight'
        >
          {t('Browse')}
        </Button>
        <Input
          id={id}
          type='file'
          accept={accept}
          ref={fileInputRef}
          onChange={handleChange}
          className={cn(
            'absolute inset-0 h-full w-full cursor-pointer opacity-0',
            disabled && 'hidden',
          )}
          disabled={disabled}
          aria-hidden='true'
          tabIndex={-1}
        />

        <span className={cn('truncate', disabled && 'opacity-50')}>
          {fileName || t('No file selected')}
        </span>
      </div>
    );
  },
);

InputFile.displayName = 'InputFile';
